import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Search, Loader2, Download, Mail, AlertCircle, Zap, X } from "lucide-react";
import { useGmailSearch, useGmailImport, useThreadSelection, useEmailMatchRules } from "@/hooks/use-gmail";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { ThreadRow } from "./import-gmail-thread/ThreadRow";

interface ImportGmailThreadModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    claimId: number;
}

export function ImportGmailThreadModal({
    open,
    onOpenChange,
    claimId,
}: ImportGmailThreadModalProps) {
    const { search, isSearching, results, reset: resetSearch } = useGmailSearch(claimId);
    const { importThread, isImporting } = useGmailImport(claimId);
    const { toast } = useToast();

    const [query, setQuery] = useState("");
    const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
    const [hasSearched, setHasSearched] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const [newRuleType, setNewRuleType] = useState<"email" | "subject">("email");
    const [newRuleValue, setNewRuleValue] = useState("");
    const { rules, isLoading: areRulesLoading, createRule, isCreating, deleteRule } = useEmailMatchRules(claimId, open && showRules);

    const threads = results?.threads ?? [];
    const {
        selection,
        toggleThread,
        toggleMessage,
        resetSelection,
        isThreadSelected,
        isMessageSelected,
        totalSelected,
        hasSelection,
    } = useThreadSelection(threads);

    // ─── Search ────────────────────────────────────────
    const handleSearch = useCallback(async () => {
        if (!query.trim()) return;
        setHasSearched(true);
        resetSelection();
        setExpandedThreads(new Set());
        try {
            await search({ query: query.trim(), maxResults: 15 });
        } catch (error) {
            toast({
                title: "Search Failed",
                description: error instanceof Error ? error.message : "Could not search Gmail",
                variant: "destructive",
            });
        }
    }, [query, search, toast, resetSelection]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearch();
        }
    };

    const toggleThreadExpanded = (threadId: string) => {
        setExpandedThreads((prev) => {
            const next = new Set(prev);
            if (next.has(threadId)) {
                next.delete(threadId);
            } else {
                next.add(threadId);
            }
            return next;
        });
    };

    // ─── Import ────────────────────────────────────────
    const handleImport = async () => {
        if (!hasSelection) return;

        let totalImported = 0;
        let totalSkipped = 0;
        const allErrors: string[] = [];

        // Import each unit (a whole thread, or specific messages within a thread)
        // independently. Wrapping each call in its own try/catch means a rejection
        // on one unit no longer aborts the rest — we still import and report
        // everything that succeeded instead of discarding partial progress.
        const importUnit = async (params: { threadId: string; messageIds?: string[] }) => {
            try {
                const result = await importThread(params);
                totalImported += result.imported;
                totalSkipped += result.skipped;
                if (result.errors) allErrors.push(...result.errors);
            } catch (error) {
                allErrors.push(error instanceof Error ? error.message : "Failed to import emails");
            }
        };

        // Import fully-selected threads
        for (const threadId of selection.threads) {
            await importUnit({ threadId });
        }

        // Import individual messages per thread
        for (const [threadId, messageIds] of selection.messages) {
            if (messageIds.size === 0) continue;
            await importUnit({ threadId, messageIds: Array.from(messageIds) });
        }

        const failedCount = allErrors.length;
        const anySucceeded = totalImported > 0 || totalSkipped > 0;
        const summary = `Imported ${totalImported} message${totalImported !== 1 ? "s" : ""}${totalSkipped > 0 ? `, ${totalSkipped} already existed` : ""}`;

        // Nothing imported and at least one failure → hard failure. Keep the modal
        // open (don't reset the selection) so the user can retry.
        if (failedCount > 0 && !anySucceeded) {
            toast({
                title: "Import Failed",
                description: allErrors[0] ?? "Could not import emails",
                variant: "destructive",
            });
            return;
        }

        // Surface partial failures: some units imported but others failed. Showing
        // only "Import Complete" would hide those errors, so we use a distinct
        // destructive toast with a count.
        if (failedCount > 0) {
            toast({
                title: "Import Completed with Errors",
                description: `${summary}. ${failedCount} failed to import.`,
                variant: "destructive",
            });
        } else {
            toast({
                title: "Import Complete",
                description: summary,
            });
        }

        // Reset and close on any success (full or partial).
        resetSelection();
        onOpenChange(false);
    };

    // ─── Auto-import rules ────────────────────────
    const handleAddRule = async () => {
        const value = newRuleValue.trim();
        if (!value) return;
        try {
            await createRule({ ruleType: newRuleType, value });
            setNewRuleValue("");
        } catch (error) {
            toast({
                title: "Could not add rule",
                description: await getApiErrorMessage(error),
                variant: "destructive",
            });
        }
    };

    const handleDeleteRule = async (ruleId: number) => {
        try {
            await deleteRule(ruleId);
        } catch (error) {
            toast({
                title: "Could not remove rule",
                description: await getApiErrorMessage(error),
                variant: "destructive",
            });
        }
    };

    // ─── Reset on close ────────────────────────────────
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setQuery("");
            resetSelection();
            setExpandedThreads(new Set());
            setHasSearched(false);
            resetSearch();
            setShowRules(false);
            setNewRuleValue("");
        }
        onOpenChange(open);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className={cn(
                    "sm:max-w-[800px] flex flex-col p-0 overflow-hidden",
                    isSearching || threads.length > 0
                        ? "h-[90dvh] md:h-[90vh]"
                        : "max-h-[90dvh] md:max-h-[90vh]",
                )}
            >
                <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <Download className="h-5 w-5" />
                        Import from Gmail
                    </DialogTitle>
                    <DialogDescription>
                        Search your Gmail to find email threads and import them into this claim.
                        You can import entire threads or select individual messages. Uses Gmail 
                        search syntax — try keywords, from:, to:, subject:, after:, before:

                    </DialogDescription>
                </DialogHeader>

                <Separator className="shrink-0" />

                <div className="min-h-0 flex-auto overflow-y-auto">
                {/* Search Bar */}
                <div className="px-6 py-3 shrink-0">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder='Search Gmail (e.g. "roof damage", from:adjuster@insurance.com, subject:claim...)'
                                className="pl-9"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={isSearching || isImporting}
                            />
                        </div>
                        <Button
                            onClick={handleSearch}
                            disabled={!query.trim() || isSearching || isImporting}
                            className="gap-2"
                        >
                            {isSearching ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Search className="h-4 w-4" />
                            )}
                            Search
                        </Button>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2 gap-1.5 px-2"
                        onClick={() => setShowRules((previous) => !previous)}
                        disabled={isImporting}
                    >
                        <Zap className="h-4 w-4" />
                        Auto-import rules{rules.length > 0 ? ` (${rules.length})` : ""}
                    </Button>
                    {showRules && (
                        <div className="mt-2 rounded-md border p-3 space-y-3">
                            <p className="text-xs text-muted-foreground">
                                Emails sent from or to a watched address, or whose subject contains a term,
                                are imported into this claim automatically on the next sync.
                            </p>
                            <div className="flex gap-2">
                                <Select
                                    value={newRuleType}
                                    onValueChange={(value) => setNewRuleType(value as "email" | "subject")}
                                    disabled={isCreating}
                                >
                                    <SelectTrigger aria-label="Rule type" className="w-[160px] shrink-0">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="email">Email address</SelectItem>
                                        <SelectItem value="subject">Subject contains</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input
                                    aria-label="Rule value"
                                    placeholder={newRuleType === "email" ? "adjuster@insurer.com" : "e.g. Hurricane Milton"}
                                    value={newRuleValue}
                                    onChange={(event) => setNewRuleValue(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            handleAddRule();
                                        }
                                    }}
                                    disabled={isCreating}
                                />
                                <Button type="button" size="sm" className="h-9" onClick={handleAddRule} disabled={isCreating || !newRuleValue.trim()}>
                                    {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                                </Button>
                            </div>
                            {areRulesLoading && <p className="text-xs text-muted-foreground">Loading rules...</p>}
                            {!areRulesLoading && rules.length === 0 && (
                                <p className="text-xs text-muted-foreground">No auto-import rules yet.</p>
                            )}
                            {rules.length > 0 && (
                                <ul className="divide-y rounded border" aria-label="Auto-import rules">
                                    {rules.map((rule) => (
                                        <li key={rule.id} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                                            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                                                {rule.ruleType === "email" ? "Address" : "Subject"}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate" title={rule.value}>{rule.value}</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteRule(rule.id)}
                                                aria-label={`Remove rule ${rule.value}`}
                                                title={`Remove rule ${rule.value}`}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                <Separator className="shrink-0" />

                {/* Results */}
                <div>
                    {/* Empty state — before search */}
                    {!hasSearched && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Mail className="h-10 w-10 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">Search your Gmail to find emails to import</p>
                        </div>
                    )}

                    {/* Empty state — no results */}
                    {hasSearched && !isSearching && threads.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">No threads found matching your search</p>
                            <p className="text-xs mt-1">Try different keywords or Gmail search operators</p>
                        </div>
                    )}

                    {/* Loading */}
                    {isSearching && (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    )}

                    {/* Thread results */}
                    {!isSearching && threads.length > 0 && (
                        <>
                            <div className="flex items-center justify-between text-xs text-muted-foreground px-6 py-3 shrink-0">
                                <span>
                                    {threads.length} thread{threads.length !== 1 ? "s" : ""} found
                                    {results?.resultSizeEstimate
                                        ? ` (~${results.resultSizeEstimate} total)`
                                        : ""}
                                </span>
                                {hasSelection && (
                                    <span className="text-primary font-medium">
                                        {totalSelected} message{totalSelected !== 1 ? "s" : ""} selected
                                    </span>
                                )}
                            </div>

                                <div className="px-6 space-y-2 pb-3">
                                    {threads.map((thread) => (
                                        <ThreadRow
                                            key={thread.threadId}
                                            thread={thread}
                                            isExpanded={expandedThreads.has(thread.threadId)}
                                            isSelected={isThreadSelected(thread.threadId)}
                                            isMessageSelected={(msgId) =>
                                                isMessageSelected(thread.threadId, msgId)
                                            }
                                            onToggleExpand={() => toggleThreadExpanded(thread.threadId)}
                                            onToggleThread={() => toggleThread(thread.threadId)}
                                            onToggleMessage={(msgId) =>
                                                toggleMessage(thread.threadId, msgId)
                                            }
                                            disabled={isImporting}
                                        />
                                    ))}
                                </div>
                        </>
                    )}
                </div>
                </div>

                {/* Footer with import button */}
                {hasSearched && threads.length > 0 && (
                    <>
                        <Separator className="shrink-0" />
                        <div className="px-6 py-4 flex shrink-0 items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                                {hasSelection
                                    ? `${totalSelected} message${totalSelected !== 1 ? "s" : ""} will be imported`
                                    : "Select threads or individual messages to import"}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => handleOpenChange(false)}
                                    disabled={isImporting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    disabled={!hasSelection || isImporting}
                                    className="gap-2"
                                >
                                    {isImporting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="h-4 w-4" />
                                            Import Selected
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
